/**
 * KaTeX's stylesheet, loaded on demand instead of on the critical path.
 *
 * WHY. `katex.min.css` (29 KB, 8 KB gzipped, plus its font-face rules) used to
 * be imported statically by the maths surfaces. One of them, the chat markdown
 * plugins, sits on the eager path, so Vite emitted it as an entry stylesheet and
 * linked it render-blocking in `index.html`: every page's first paint waited
 * for maths styles most pages never use. Measured 2026-09-26 on simulated slow
 * 3G (400 ms, 50 KB/s): the pre-JavaScript splash could not paint until ~2.5 s
 * although the HTML had arrived at ~0.5 s.
 *
 * HOW. A dynamic `import()` of the CSS makes Vite emit it as its own file and
 * inject the `<link>` only when this runs. Every surface that renders maths
 * calls it as it does, and `App.tsx` also warms it in an idle period once the
 * user is signed in, so it is almost always cached before the first formula
 * appears. Signed-out visits (Login, password reset, email actions) render no
 * maths and never fetch it.
 *
 * WHY THE WARM MATTERS. A formula rendered before the sheet lands shows in
 * plain type for a moment (never doubled: the global theme carries KaTeX's
 * own rule hiding its MathML copy). The idle warm makes that the rare case
 * (maths on screen within moments of a cold load) rather than the rule. A
 * signed-out public share page is not warmed; it asks for the sheet as it
 * builds its editor, so it can show that moment.
 *
 * A failed load (a network blip, or a stale chunk after a deploy) resolves
 * anyway, so maths renders unstyled, which is legible, rather than rejecting
 * into the global stale-chunk reload handler. It is NOT retried: Vite's
 * preload helper marks a sheet as seen before fetching it, so a second
 * `import()` would skip the fetch and report success. The failure is logged
 * instead, so it is not silent; a reload fetches it again.
 */

let pending: Promise<void> | null = null;

export function loadKatexStyles(): Promise<void> {
  pending ??= import('katex/dist/katex.min.css').then(
    () => undefined,
    (error: unknown) => {
      console.warn('KaTeX stylesheet failed to load; maths will render unstyled:', error);
    },
  );
  return pending;
}
